import type { FetchArgType } from "openapi-typescript-fetch";
import type {
    GetListParams,
    GetListResult,
    GetManyReferenceParams,
    GetManyReferenceResult,
    UpdateParams,
    UpdateManyParams,
    CreateParams,
    DeleteManyParams,
    DeleteManyResult,
    UpdateResult,
    GetManyResult,
    CreateResult
} from "react-admin";
import { isEmpty } from "lodash";

import { fetcher } from "../../data";
import { isDefined } from "../../utils/typedLoDashUtils";
import { assertIsDefined } from "../../utils/assertions";
import type { BaseFilters } from "../../data/BaseFilters";
import { NotImplementedError } from "../../utils/NotImplementedError";
import type { ErrorDetails, LangParams, MpmDataProvider, MpmDataProviderOperationName } from "../../data";

import type { ContentTypeDTO } from "./ContentTypeDTO";
import type { ContentTypeRecord } from "./ContentTypeRecord";
import { convertIdFilter } from "../../utils/filterUtils";

const findAllTranslated = fetcher.path("/api/content/content-type/findAll").method("post").create();
const getTranslated = fetcher.path("/api/content/content-type/{uuid}").method("get").create();
const updateOne = fetcher.path("/api/content/content-type/{uuid}").method("put").create();
const createOne = fetcher.path("/api/content/content-type/").method("post").create();
const deleteOne = fetcher.path("/api/content/content-type/{uuid}").method("delete").create();
const getAllByUuids = fetcher.path("/api/content/content-type/allByUuids").method("get").create();

/**
 * Parses the DTO from backend to record for frontend
 * @param dto
 * @returns ContentTypeRecord
 */

function mapDTOToRecord(dto: ContentTypeDTO): ContentTypeRecord {
    // DTO from backend
    const { id = 0, uuid = "", name = "", description = "" } = dto;

    // Record for front-end
    return {
        shortId: id,
        id: uuid,
        name,
        description
    };
}

/**
 * Parses the record from frontend to DTO for backend
 * @param record
 * @returns ContentTypeDTO
 */

function mapRecordToDTO(record: ContentTypeRecord): ContentTypeDTO {
    // Record from front-end
    const { shortId = 0, id = "", name = "", description = "" } = record;

    // DTO for back-end
    return {
        id: shortId,
        name,
        uuid: id as string,
        description
    };
}

interface ContentTypeFilters extends BaseFilters {
    q?: string;
    id: string;
}

async function getList(params: Readonly<GetListParams>): Promise<GetListResult<ContentTypeRecord>> {
    const { q: searchTerms, ...filters } = params.filter as ContentTypeFilters;

    let parameters: FetchArgType<typeof findAllTranslated> = {
        filter: convertIdFilter(filters),
        pageable: {
            page: params.pagination.page - 1,
            size: params.pagination.perPage,
            sort: isEmpty(params.sort.field) ? [] : [`${params.sort.field},${params.sort.order}`]
        }
    } as const;

    if (isDefined(searchTerms)) {
        parameters = {
            ...parameters,
            filter: { ...parameters.filter, searchTerms }
        };
    }

    const { data } = await findAllTranslated(parameters);
    const dataPage = data.content;
    assertIsDefined(dataPage);
    const total = dataPage.totalElements;
    assertIsDefined(total);
    assertIsDefined(dataPage.content);

    return {
        data: dataPage.content.map((content) => mapDTOToRecord({ id: content.id ?? 0, ...content })),
        total
    };
}

async function getOne(params: LangParams | (number | string)): Promise<ContentTypeRecord> {
    const {
        data: { content: userDTO }
    } = await getTranslated({
        uuid: params as string
    });
    assertIsDefined(userDTO);

    return mapDTOToRecord({ id: userDTO.id ?? 0, ...userDTO });
}

async function getMany(uuids: readonly (number | string)[]): Promise<GetManyResult<ContentTypeRecord>> {
    const {
        data: { content: userDTO }
    } = await getAllByUuids({
        uuids: uuids as string[]
    });
    assertIsDefined(userDTO);

    return { data: await Promise.all(userDTO.map((dto) => mapDTOToRecord({ id: dto.id ?? 0, ...dto }))) };
}

async function getManyReference(_params: Readonly<GetManyReferenceParams>): Promise<GetManyReferenceResult<ContentTypeRecord>> {
    return Promise.reject(new NotImplementedError("getManyReference"));
}

async function update(params: Readonly<UpdateParams<ContentTypeRecord>>): Promise<UpdateResult<ContentTypeRecord>> {
    const payload = mapRecordToDTO(params.data as ContentTypeRecord);

    const parameters = { uuid: payload.uuid ?? "", ...payload };

    const { data } = await updateOne(parameters);
    const contentTypeDTO = data.content;
    assertIsDefined(contentTypeDTO);

    return { data: mapDTOToRecord({ id: parameters.id, ...contentTypeDTO }) };
}

async function updateMany(_params: Readonly<UpdateManyParams>): Promise<string[]> {
    return Promise.reject(new NotImplementedError("updateMany"));
}

async function create(params: Readonly<CreateParams>): Promise<CreateResult<ContentTypeRecord>> {
    const payload = mapRecordToDTO(params.data as any);

    assertIsDefined(payload.name);

    const parameters = { name: payload.name, description: payload.description ?? "" };
    const { data } = await createOne(parameters);
    const contentTypeDTO = data.content;

    assertIsDefined(contentTypeDTO);

    return { data: mapDTOToRecord({ id: contentTypeDTO.id as number, ...contentTypeDTO }) };
}

// eslint-disable-next-line no-underscore-dangle
async function _delete(uuid: number | string): Promise<ContentTypeRecord> {
    const {
        data: { content: isDeleted }
    } = await deleteOne({
        uuid: uuid as string
    });
    assertIsDefined(isDeleted);

    return {} as ContentTypeRecord;
}

async function deleteMany(params: Readonly<DeleteManyParams>): Promise<DeleteManyResult> {
    await Promise.all(params.ids.map((id: string) => _delete(id)));
    return {
        data: params.ids.map((id: string) => {
            id;
        })
    };
}

function getErrorDetails(_error: unknown, _operation: MpmDataProviderOperationName<ContentTypeRecord>): ErrorDetails | undefined {
    // eslint-disable-next-line no-undefined
    return undefined;
}

export const ContentTypeDataProvider: MpmDataProvider<ContentTypeRecord> = {
    create,
    delete: _delete,
    deleteMany,
    getErrorDetails,
    getList,
    getMany,
    getManyReference,
    getOne,
    update,
    updateMany
};
